import { useLocation, useNavigate } from 'react-router';
import { useCallback, useState } from 'react';
import { useChannel } from '../hooks/store/useChannelHooks';
import { CareTeamInfoCollapsed } from '../channel/CareTeamInfo';
import { Divider } from '@mui/material';
import { Messaging } from '../messaging/Messaging';
import { MessagingHeaderStandalone } from '../messaging/MessagingHeader';
import { MessageBarContainer } from './MessageBarContainer';
import { MessageBarHeader } from './MessageBarHeader';
import {
  CareTeamTab,
  CommunityTab,
  MessageBarTabView,
  MessageTab,
} from './MessageBarTabView';
import { FileGallery } from '../messaging/gallery/FileGallery';
import { messageNavigationState } from '../../container/MessageContainerNavigationState';
import { ChannelMembers } from '../messaging/member/ChannelMembers';
import { Routes } from '@xolv-mybrightlink/common';
import { useMessageStandaloneDisplayConfig } from './MessageStandaloneDisplayConfigProvider';

export type MessageStandaloneViewMode =
  | 'channels'
  | 'info'
  | 'messaging'
  | 'gallery'
  | 'members';

export type MessageBarExchangeMode = Extract<
  MessageStandaloneViewMode,
  'messaging' | 'gallery' | 'members'
>;

export const MessageBarStandalone = () => {
  const location = useLocation();
  window.history.replaceState(null, '');
  const { displayConfig, displayConfigDispatch } =
    useMessageStandaloneDisplayConfig();
  const {
    viewMode,
    selectedChannelGuid: channelGuid,
    activeTab,
  } = displayConfig;
  const onChangeTab = (event: React.SyntheticEvent, newTab: MessageTab) => {
    displayConfigDispatch({
      type: 'CHANGE_TAB',
      payload: {
        newTab,
      },
    });
  };
  const channel = useChannel(channelGuid ?? '');
  const [patientGuid, setPatientGuid] = useState<string | undefined>(
    channel?.attributes?.patientGuid,
  );
  const navigate = useNavigate();
  const toggleInfo = () => {
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: { viewMode: viewMode === 'info' ? 'channels' : 'info' },
    });
  };
  const expandMessages = useCallback(() => {
    messageNavigationState.collapsedPath = location.pathname;
    if (viewMode === 'info') {
      navigate(Routes.messages.info);
    } else if (activeTab === CommunityTab) {
      navigate(Routes.messages.community.index);
    } else {
      navigate(Routes.messages.index);
    }
  }, [location, viewMode, navigate, activeTab]);
  const openMessaging = (channelGuid: string) => {
    displayConfigDispatch({
      type: 'START_CONVERSATION',
      payload: {
        selectedChannelGuid: channelGuid,
        activeTab: activeTab,
      },
    });
  };
  const closeMessaging = () => {
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: { viewMode: 'channels' },
    });
  };
  const openGallery = () => {
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: { viewMode: 'gallery' },
    });
  };
  const openChannelMember = () => {
    displayConfigDispatch({
      type: 'CHANGE_VIEW_MODE',
      payload: { viewMode: 'members' },
    });
  };

  return (
    <MessageBarContainer standalone sx={{ mt: 2.5 }}>
      {viewMode === 'info' && (
        <CareTeamInfoCollapsed onClose={toggleInfo} onExpand={expandMessages} />
      )}
      {viewMode === 'channels' && (
        <>
          <MessageBarHeader
            showInfoIcon={activeTab === CareTeamTab}
            toggleInfo={toggleInfo}
            toggleMode={expandMessages}
            standalone
          />
          <Divider sx={{ height: '10px', borderBottomWidth: 0 }} />
          <MessageBarTabView
            defaultTab={activeTab}
            onChangeTab={onChangeTab}
            defaultPatientGuid={patientGuid}
            onSelectChannel={openMessaging}
            onSelectPatient={setPatientGuid}
            standalone
          />
        </>
      )}
      {viewMode === 'messaging' && channelGuid && (
        <Messaging
          key={channelGuid}
          channelGuid={channelGuid}
          header={
            <MessagingHeaderStandalone
              channelGuid={channelGuid}
              onClose={closeMessaging}
              onOpenGallery={openGallery}
              openChannelMember={openChannelMember}
            />
          }
          standalone
        />
      )}
      {viewMode === 'gallery' && channelGuid && (
        <FileGallery
          channelGuid={channelGuid}
          standalone
          onClose={() => openMessaging(channelGuid)}
        />
      )}
      {viewMode === 'members' && channelGuid && (
        <ChannelMembers
          channelGuid={channelGuid}
          standalone
          onClose={() => openMessaging(channelGuid)}
        />
      )}
    </MessageBarContainer>
  );
};
